import { useApolloClient } from '@apollo/react-hooks';
import XLSX from 'xlsx';

import { formatSchoolTerm } from '../utils';
import { getLearningResultReportExport } from '../../GraphQL/queries/report.query.js';

// Round percentage values to 2 decimals; leave non-numbers untouched.
const round2 = value =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 100) / 100 : value;

const NO_DATA_LABEL = 'ไม่มีข้อมูล';

// Toggle the egg columns (ไข่รายคน แยกวิชา / รวมทุกวิชา) in the "ภาพรวมรายคน" sheet.
// Eggs are a subject/accumulated metric, so they belong on the unit-level student
// sheet only (not the per-lesson sheet, where the value would repeat every row).
const INCLUDE_EGG_COLUMNS = true;

// "ทุกห้อง" aggregate rows come back with classNo = null.
const ALL_ROOMS_LABEL = 'ทุกห้อง';
const roomText = (schoolGrade, classNo) =>
  classNo === null || classNo === undefined ? ALL_ROOMS_LABEL : `${schoolGrade}/${classNo}`;

const convertSheet1ToAOA = data => {
  const {
    schoolTerm: { shortName: schoolTerm },
    schoolGrade: { shortName: schoolGrade },
    subject: { name: subject },
    sheet1,
  } = data;

  const sheet1Report = [];

  for (const [index, item] of sheet1.entries()) {
    sheet1Report[index] = [];
    const { classNo, courseName, LV1, LV2, LV3, LV4, C1, C2, C3, C4 } = item;

    sheet1Report[index].push(
      ...[
        schoolTerm,
        schoolGrade,
        classNo ?? ALL_ROOMS_LABEL,
        subject,
        courseName,
        round2(LV1),
        round2(LV2),
        round2(LV3),
        round2(LV4),
        round2(C1),
        round2(C2),
        round2(C3),
        round2(C4),
      ]
    );
  }

  return sheet1Report;
};

const convertSheet2ToAOA = data => {
  const {
    schoolTerm: { shortName: schoolTerm },
    schoolGrade: { shortName: schoolGrade },
    subject: { name: subject },
    sheet2,
  } = data;

  const sheet2Report = [];

  for (const [index, item] of sheet2.entries()) {
    sheet2Report[index] = [];
    const { classNo, courseName, none, beginner, intermediate, advanced, expert } = item;

    sheet2Report[index].push(
      ...[
        schoolTerm,
        schoolGrade,
        classNo ?? ALL_ROOMS_LABEL,
        subject,
        courseName,
        round2(none),
        round2(beginner),
        round2(intermediate),
        round2(advanced),
        round2(expert),
      ]
    );
  }

  return sheet2Report;
};

const calculateGainScoreCategory = score => {
  if (score > 75) return 'ระดับสูงมาก';
  if (score > 50) return 'ระดับสูง';
  if (score > 25) return 'ระดับกลาง';
  if (score > 0) return 'ระดับต้น';
  return 'ไม่มี';
};

const convertMasteryTextToType = {
  LV1: 'เริ่มตั้งไข่',
  LV2: 'ตั้งไข่',
  LV3: 'เข้าใจ',
  LV4: 'เชี่ยวชาญ',
};

const convertSheet3ToAOA = (data, students) => {
  const {
    schoolTerm: { shortName: schoolTerm },
    schoolGrade: { shortName: schoolGrade },
    subject: { name: subject },
  } = data;

  const sheet3Report = [];

  for (const [index, item] of students.entries()) {
    sheet3Report[index] = [];
    const {
      classNo,
      courseName,
      studentNo,
      classroomUsername,
      firstName,
      lastName,
      mastery,
      bloom,
      egg,
      eggTotal,
    } = item;

    const classNoText = roomText(schoolGrade, classNo);
    const fullName = `${firstName} ${lastName}`;

    sheet3Report[index].push(
      ...[
        schoolTerm,
        schoolGrade,
        classNoText,
        subject,
        courseName,
        studentNo,
        classroomUsername,
        fullName,
        convertMasteryTextToType[mastery] ?? NO_DATA_LABEL,
        bloom ?? NO_DATA_LABEL,
        ...(INCLUDE_EGG_COLUMNS ? [egg ?? NO_DATA_LABEL, eggTotal ?? NO_DATA_LABEL] : []),
      ]
    );
  }

  return sheet3Report;
};

const convertSheet4ToAOA = (data, students) => {
  const {
    schoolTerm: { shortName: schoolTerm },
    schoolGrade: { shortName: schoolGrade },
    subject: { name: subject },
  } = data;

  const sheet4Report = [];

  for (const [index, item] of students.entries()) {
    sheet4Report[index] = [];
    const {
      classNo,
      courseName,
      studentNo,
      classroomUsername,
      firstName,
      lastName,
      pretest,
      posttest,
      growthRate,
      prerequisite,
      pisa,
    } = item;

    const classNoText = roomText(schoolGrade, classNo);
    const fullName = `${firstName} ${lastName}`;

    sheet4Report[index].push(
      ...[
        schoolTerm,
        schoolGrade,
        classNoText,
        subject,
        courseName,
        studentNo,
        classroomUsername,
        fullName,
        round2(pretest),
        round2(prerequisite),
        round2(posttest),
        round2(pisa),
        round2(growthRate),
        calculateGainScoreCategory(growthRate),
      ]
    );
  }

  return sheet4Report;
};

const formatSheet1ToAOA = data => {
  const progressHeader = [
    'ระดับความเชี่ยวชาญ (%)',
    'ระดับความเชี่ยวชาญ (%)',
    'ระดับความเชี่ยวชาญ (%)',
    'ระดับความเชี่ยวชาญ (%)',
    "Bloom's Taxonomy (%)",
    "Bloom's Taxonomy (%)",
    "Bloom's Taxonomy (%)",
    "Bloom's Taxonomy (%)",
  ];
  const masteryAOA = ['เริ่มตั้งไข่', 'ตั้งไข่', 'เข้าใจ', 'เชี่ยวชาญ'];
  const bloomAOA = ['C1', 'C2', 'C3', 'C4'];
  const progressHeaderTypes = [...masteryAOA, ...bloomAOA];

  let formattedSheet1AOA = [
    ['', '', '', '', ''],
    ['เทอม', 'ระดับชั้น', 'ห้อง', 'วิชา', 'หน่วยการเรียนรู้'],
  ];
  formattedSheet1AOA[0].push(...progressHeader);
  formattedSheet1AOA[1].push(...progressHeaderTypes);

  const sheet1Report = convertSheet1ToAOA(data);

  formattedSheet1AOA.push(...sheet1Report);

  return formattedSheet1AOA;
};

const formatSheet2ToAOA = data => {
  const progressHeader = [
    'ระดับพัฒนาการ (%)',
    'ระดับพัฒนาการ (%)',
    'ระดับพัฒนาการ (%)',
    'ระดับพัฒนาการ (%)',
    'ระดับพัฒนาการ (%)',
  ];
  const gainScoreAOA = [
    'ไม่มีพัฒนาการ',
    'พัฒนาการระดับต้น',
    'พัฒนาการระดับกลาง',
    'พัฒนาการระดับสูง',
    'พัฒนาการระดับสูงมาก',
  ];

  let formattedSheet2AOA = [
    ['', '', '', '', ''],
    ['เทอม', 'ระดับชั้น', 'ห้อง', 'วิชา', 'หน่วยการเรียนรู้'],
  ];
  formattedSheet2AOA[0].push(...progressHeader);
  formattedSheet2AOA[1].push(...gainScoreAOA);

  const sheet2Report = convertSheet2ToAOA(data);

  formattedSheet2AOA.push(...sheet2Report);

  return formattedSheet2AOA;
};

const formatSheet3ToAOA = (data, students) => {
  const formattedSheet3AOA = [
    [
      'เทอม',
      'ระดับชั้น',
      'ห้อง',
      'วิชา',
      'หน่วยการเรียนรู้',
      'เลขที่',
      'รหัสผู้ใช้',
      'ชื่อ-นามสกุล',
      'ระดับความเชี่ยวชาญ',
      "Bloom's Taxonomy",
      ...(INCLUDE_EGG_COLUMNS ? ['ไข่รายคน แยกวิชา', 'ไข่รายคน รวมทุกวิชา'] : []),
    ],
  ];

  const sheet3Report = convertSheet3ToAOA(data, students);

  formattedSheet3AOA.push(...sheet3Report);

  return formattedSheet3AOA;
};

const formatSheet4ToAOA = (data, students) => {
  const formattedSheet4AOA = [
    [
      'เทอม',
      'ระดับชั้น',
      'ห้อง',
      'วิชา',
      'หน่วยการเรียนรู้',
      'เลขที่',
      'รหัสผู้ใช้',
      'ชื่อ-นามสกุล',
      'แบบทดสอบก่อนเรียน',
      'Prerequisite',
      'แบบทดสอบหลังเรียน',
      'PISA',
      '% พัฒนาการ',
      'ระดับพัฒนาการ',
    ],
  ];

  const sheet4Report = convertSheet4ToAOA(data, students);

  formattedSheet4AOA.push(...sheet4Report);

  return formattedSheet4AOA;
};

const createSheet1Sheet = formattedAOA => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedAOA);

  // ['เทอม', 'ระดับชั้น', 'ห้อง', 'วิชา']
  worksheet['!cols'] = Array.from({ length: 4 }, () => ({ wch: 15 }));

  // ['หน่วยการเรียนรู้']
  worksheet['!cols'].push(...Array.from({ length: 1 }, () => ({ wch: 20 })));

  // ['เริ่มตั้งไข่', 'เริ่มทำได้', 'เริ่มเชี่ยวชาญ', 'พร้อมสอบ',
  // 'C1', 'C2', 'C3', 'C4']
  worksheet['!cols'].push(...Array.from({ length: 8 }, () => ({ wch: 15 })));

  worksheet['!merges'] = [
    { s: { r: 0, c: 5 }, e: { r: 0, c: 8 } }, // ระดับความเชี่ยวชาญ (%)
    { s: { r: 0, c: 9 }, e: { r: 0, c: 12 } }, // Bloom (%)
  ];

  return worksheet;
};

const createSheet2Sheet = formattedAOA => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedAOA);

  // ['เทอม', 'ระดับชั้น', 'ห้อง', 'วิชา']
  worksheet['!cols'] = Array.from({ length: 4 }, () => ({ wch: 15 }));

  // ['หน่วยการเรียนรู้']
  worksheet['!cols'].push({ wch: 20 });

  // ['ไม่มีพัฒนาการ', 'พัฒนาการระดับต้น', 'พัฒนาการระดับกลาง', 'พัฒนาการระดับสูง', 'พัฒนาการระดับสูงมาก']
  worksheet['!cols'].push(...Array.from({ length: 5 }, () => ({ wch: 15 })));

  worksheet['!merges'] = [
    { s: { r: 0, c: 5 }, e: { r: 0, c: 9 } }, // ระดับพัฒนาการ (%)
  ];

  return worksheet;
};

const createSheet3Sheet = formattedAOA => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedAOA);

  // ['เทอม', 'ระดับชั้น', 'ห้อง', 'วิชา']
  worksheet['!cols'] = Array.from({ length: 4 }, () => ({ wch: 15 }));

  // ['หน่วยการเรียนรู้']
  worksheet['!cols'].push(...Array.from({ length: 1 }, () => ({ wch: 20 })));

  // ['เลขที่', 'รหัสผู้ใช้', 'ชื่อ-นามสกุล', 'ระดับความเชี่ยวชาญ', 'Bloom's Taxonomy (%)']
  // + optional egg columns (ไข่รายคน แยกวิชา / รวมทุกวิชา)
  worksheet['!cols'].push(
    ...Array.from({ length: INCLUDE_EGG_COLUMNS ? 7 : 5 }, () => ({ wch: 15 }))
  );

  return worksheet;
};

const createSheet4Sheet = formattedAOA => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedAOA);

  // ['เทอม', 'ระดับชั้น', 'ห้อง', 'วิชา']
  worksheet['!cols'] = Array.from({ length: 4 }, () => ({ wch: 15 }));

  // ['หน่วยการเรียนรู้']
  worksheet['!cols'].push({ wch: 20 });

  // ['เลขที่', 'รหัสผู้ใช้', 'ชื่อ-นามสกุล', 'แบบทดสอบก่อนเรียน', 'Prerequisite' 'แบบทดสอบหลังเรียน', 'PISA', '% พัฒนาการ', 'ระดับพัฒนาการ']
  worksheet['!cols'].push(...Array.from({ length: 9 }, () => ({ wch: 15 })));

  return worksheet;
};

// Map courseCode -> courseName using the class-level sheets (sheet1/sheet3),
// so the lesson sheets can show หน่วยการเรียนรู้ without a new Data-team column.
const buildCourseNameByCode = data => {
  const map = {};
  for (const item of [...(data.sheet1 ?? []), ...(data.sheet3 ?? [])]) {
    if (item.courseCode && item.courseName) {
      map[item.courseCode] = item.courseName;
    }
  }
  return map;
};

const convertLessonClassSheetToAOA = (data, rows, courseNameByCode) => {
  const {
    schoolTerm: { shortName: schoolTerm },
    schoolGrade: { shortName: schoolGrade },
    subject: { name: subject },
  } = data;

  return rows.map(item => {
    const { classNo, courseCode, lessonName, LV1, LV2, LV3, LV4, C1, C2, C3, C4 } = item;
    const courseName = courseNameByCode[courseCode] ?? data.courseName ?? '';
    return [
      schoolTerm,
      schoolGrade,
      classNo ?? ALL_ROOMS_LABEL,
      subject,
      courseName,
      lessonName ?? NO_DATA_LABEL,
      round2(LV1),
      round2(LV2),
      round2(LV3),
      round2(LV4),
      round2(C1),
      round2(C2),
      round2(C3),
      round2(C4),
    ];
  });
};

const formatLessonClassSheetToAOA = (data, rows, courseNameByCode) => {
  const progressHeader = [
    'ระดับความเชี่ยวชาญ (%)',
    'ระดับความเชี่ยวชาญ (%)',
    'ระดับความเชี่ยวชาญ (%)',
    'ระดับความเชี่ยวชาญ (%)',
    "Bloom's Taxonomy (%)",
    "Bloom's Taxonomy (%)",
    "Bloom's Taxonomy (%)",
    "Bloom's Taxonomy (%)",
  ];
  const masteryAOA = ['เริ่มตั้งไข่', 'ตั้งไข่', 'เข้าใจ', 'เชี่ยวชาญ'];
  const bloomAOA = ['C1', 'C2', 'C3', 'C4'];
  const progressHeaderTypes = [...masteryAOA, ...bloomAOA];

  const formatted = [
    ['', '', '', '', '', ''],
    ['เทอม', 'ระดับชั้น', 'ห้อง', 'วิชา', 'หน่วยการเรียนรู้', 'บทเรียน'],
  ];
  formatted[0].push(...progressHeader);
  formatted[1].push(...progressHeaderTypes);
  formatted.push(...convertLessonClassSheetToAOA(data, rows, courseNameByCode));
  return formatted;
};

const createLessonClassSheet = formattedAOA => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedAOA);
  // เทอม, ระดับชั้น, ห้อง, วิชา
  worksheet['!cols'] = Array.from({ length: 4 }, () => ({ wch: 15 }));
  // หน่วยการเรียนรู้, บทเรียน
  worksheet['!cols'].push({ wch: 20 }, { wch: 20 });
  // mastery(4) + bloom(4)
  worksheet['!cols'].push(...Array.from({ length: 8 }, () => ({ wch: 15 })));
  worksheet['!merges'] = [
    { s: { r: 0, c: 6 }, e: { r: 0, c: 9 } }, // ระดับความเชี่ยวชาญ (%)
    { s: { r: 0, c: 10 }, e: { r: 0, c: 13 } }, // Bloom (%)
  ];
  return worksheet;
};

const convertLessonStudentSheetToAOA = (data, rows, courseNameByCode) => {
  const {
    schoolTerm: { shortName: schoolTerm },
    schoolGrade: { shortName: schoolGrade },
    subject: { name: subject },
  } = data;

  return rows.map(item => {
    const {
      classNo,
      courseCode,
      lessonName,
      studentNo,
      classroomUsername,
      firstName,
      lastName,
      mastery,
      bloom,
    } = item;
    const courseName = courseNameByCode[courseCode] ?? data.courseName ?? '';
    return [
      schoolTerm,
      schoolGrade,
      roomText(schoolGrade, classNo),
      subject,
      courseName,
      lessonName ?? NO_DATA_LABEL,
      studentNo,
      classroomUsername,
      `${firstName} ${lastName}`,
      convertMasteryTextToType[mastery] ?? NO_DATA_LABEL,
      bloom ?? NO_DATA_LABEL,
    ];
  });
};

const formatLessonStudentSheetToAOA = (data, rows, courseNameByCode) => {
  const formatted = [
    [
      'เทอม',
      'ระดับชั้น',
      'ห้อง',
      'วิชา',
      'หน่วยการเรียนรู้',
      'บทเรียน',
      'เลขที่',
      'รหัสผู้ใช้',
      'ชื่อ-นามสกุล',
      'ระดับความเชี่ยวชาญ',
      "Bloom's Taxonomy",
    ],
  ];
  formatted.push(...convertLessonStudentSheetToAOA(data, rows, courseNameByCode));
  return formatted;
};

const createLessonStudentSheet = formattedAOA => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedAOA);
  // เทอม, ระดับชั้น, ห้อง, วิชา
  worksheet['!cols'] = Array.from({ length: 4 }, () => ({ wch: 15 }));
  // หน่วยการเรียนรู้, บทเรียน
  worksheet['!cols'].push({ wch: 20 }, { wch: 20 });
  // เลขที่, รหัสผู้ใช้, ชื่อ-นามสกุล, ระดับความเชี่ยวชาญ, Bloom
  worksheet['!cols'].push(...Array.from({ length: 5 }, () => ({ wch: 15 })));
  return worksheet;
};

const ExportLearningResultReportData = props => {
  const {
    schoolTermId,
    schoolGradeId,
    classNo,
    subjectId,
    courseCode,
    lessonId,
    schoolCode,
    children,
    activeBar,
  } = props;
  const client = useApolloClient();

  const fetchLearningResultReportData = async () => {
    try {
      const { data } = await client.query({
        query: getLearningResultReportExport,
        variables: {
          schoolTermId,
          schoolGradeId,
          classNo,
          subjectId,
          schoolCode,
          courseCode,
          lessonId: lessonId && lessonId !== `all-lesson-selected` ? lessonId : null,
        },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const exportLearningResultReport = async ({ data }) => {
    const {
      schoolTerm: { shortName: schoolTerm },
      schoolGrade: { shortName: schoolGrade },
      subject: { name: subject },
      courseName,
      sheet3,
      sheet4,
      lessonClassSheet = [],
      lessonStudentSheet = [],
    } = data;

    const headerClassNo = classNo ?? `ทุกห้อง`;
    const headerCourseName = courseName ?? 'ทุกหน่วยการเรียนรู้';
    const courseNameByCode = buildCourseNameByCode(data);
    // Lesson name for the filename when a single lesson is exported.
    const selectedLessonName =
      lessonId && lessonId !== `all-lesson-selected`
        ? lessonStudentSheet[0]?.lessonName ?? lessonClassSheet[0]?.lessonName ?? null
        : null;

    let studentsSheet3 = sheet3;
    let studentsSheet4 = sheet4;
    if (activeBar?.type === 'GAIN_SCORE') {
      studentsSheet4 = [...sheet4].filter(student => {
        const { growthRate } = student;
        const filterGainScore = activeBar?.label === calculateGainScoreCategory(growthRate);
        return filterGainScore;
      });

      const usernames = new Set(studentsSheet4.map(s => s.classroomUsername));

      studentsSheet3 = [...sheet3].filter(s => usernames.has(s.classroomUsername));
    }

    if (['MASTERY', 'BLOOM'].includes(activeBar?.type)) {
      studentsSheet3 = [...sheet3].filter(student => {
        const { mastery, bloom } = student;
        const filterMastery = activeBar?.label === convertMasteryTextToType[mastery];
        const filterBloom = bloom?.includes(activeBar?.label);
        return filterMastery || filterBloom;
      });

      const usernames = new Set(studentsSheet3.map(s => s.classroomUsername));

      studentsSheet4 = [...sheet4].filter(s => usernames.has(s.classroomUsername));
    }

    // Lesson student sheet is filtered the same way as sheet4 for MASTERY/BLOOM.
    // GAIN_SCORE does not apply (no growth at lesson level).
    let lessonStudents = lessonStudentSheet;
    if (['MASTERY', 'BLOOM'].includes(activeBar?.type)) {
      lessonStudents = [...lessonStudentSheet].filter(student => {
        const { mastery, bloom } = student;
        const filterMastery = activeBar?.label === convertMasteryTextToType[mastery];
        const filterBloom = bloom?.includes(activeBar?.label);
        return filterMastery || filterBloom;
      });
    }

    const sheet1ToAOA = formatSheet1ToAOA(data);
    const sheet1Sheet = createSheet1Sheet(sheet1ToAOA);

    const lessonClassToAOA = formatLessonClassSheetToAOA(data, lessonClassSheet, courseNameByCode);
    const lessonClassWorksheet = createLessonClassSheet(lessonClassToAOA);

    const sheet2ToAOA = formatSheet2ToAOA(data);
    const sheet2Sheet = createSheet2Sheet(sheet2ToAOA);

    const sheet3ToAOA = formatSheet3ToAOA(data, studentsSheet3);
    const sheet3Sheet = createSheet3Sheet(sheet3ToAOA);

    const lessonStudentToAOA = formatLessonStudentSheetToAOA(
      data,
      lessonStudents,
      courseNameByCode
    );
    const lessonStudentWorksheet = createLessonStudentSheet(lessonStudentToAOA);

    const sheet4ToAOA = formatSheet4ToAOA(data, studentsSheet4);
    const sheet4Sheet = createSheet4Sheet(sheet4ToAOA);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, sheet1Sheet, 'ภาพรวมระดับชั้น');
    XLSX.utils.book_append_sheet(workbook, lessonClassWorksheet, 'ภาพรวมระดับชั้น บทเรียน');
    XLSX.utils.book_append_sheet(workbook, sheet2Sheet, 'ภาพรวมระดับชั้น % พัฒนาการ');
    XLSX.utils.book_append_sheet(workbook, sheet3Sheet, 'ภาพรวมรายคน');
    XLSX.utils.book_append_sheet(workbook, lessonStudentWorksheet, 'ภาพรวมรายคน บทเรียน');
    XLSX.utils.book_append_sheet(workbook, sheet4Sheet, 'ภาพรวมรายคน % พัฒนาการ');

    const lessonNamePart = selectedLessonName ? `_${selectedLessonName}` : '';
    const filename = `ผลลัพธ์การเรียนรู้_${formatSchoolTerm(
      schoolTerm
    )}_${schoolGrade}_${headerClassNo}_${subject}_${headerCourseName}${lessonNamePart}${
      activeBar?.label ? `_${activeBar?.label}` : ''
    }`;

    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  return children({
    fetchLearningResultReportData,
    exportLearningResultReport,
  });
};

export default ExportLearningResultReportData;
